import { defineColumns } from '@juvare/platform-shared/features/storage';
import { adjustZodSchemaDefaultForUnknownFields, zodKnownFieldDefaultValidation } from '@juvare/platform-shared/features/validation';
import { z } from 'zod';
import { AddressSchema } from '../Address';
export const accountEntityName = 'account';
export const systemStatuses = ['active', 'inactive', 'closed', 'deleted'];
export const phoneNumberTypes = ['voice', 'text', 'fax'];
export const emergencyContactRelationship = [
    'child',
    'domesticPartner',
    'parent',
    'sibling',
    'otherRelative',
    'spouse',
    'coWorker',
    'relative',
    'friend',
    'other',
];
export const ContactEmailSchema = z.object({
    email: z.string().min(1).max(zodKnownFieldDefaultValidation.stringMax),
});
export const ContactPhoneNumberSchema = z.object({
    phoneNumber: z.string().min(1).max(zodKnownFieldDefaultValidation.stringMax),
    type: z
        .record(z.enum(phoneNumberTypes), z.boolean())
        .refine(zodKnownFieldDefaultValidation.recordRefineCondition, zodKnownFieldDefaultValidation.recordRefineErrorMessage),
});
export const EmergencyContactSchema = z.object({
    name: z.string().min(1).max(zodKnownFieldDefaultValidation.stringMax).optional(),
    relationship: z.string().max(zodKnownFieldDefaultValidation.stringMax).optional(), // Allow custom or empty relationship values - predefined values can be found in `emergencyContactRelationship` list
    phoneNumbers: ContactPhoneNumberSchema.array().max(zodKnownFieldDefaultValidation.arrayMax),
});
export const EmergencyContactContainerSchema = z.object({
    emergencyContact: EmergencyContactSchema,
});
export const PagerNumberSchema = z.object({
    number: z.coerce.string().max(zodKnownFieldDefaultValidation.stringMax),
    carrier: z.string().min(1).max(zodKnownFieldDefaultValidation.stringMax),
});
export const accountSchema = adjustZodSchemaDefaultForUnknownFields(z.object({
    email: z.string().min(1).max(zodKnownFieldDefaultValidation.stringMax),
    firstName: z.string().min(0).max(zodKnownFieldDefaultValidation.stringMax),
    lastName: z.string().min(0).max(zodKnownFieldDefaultValidation.stringMax),
    systemRole: z.string().min(1).max(zodKnownFieldDefaultValidation.stringMax),
    systemStatus: z.enum(systemStatuses),
    orgRole: z.record(z.string().uuid(), z.string().min(1).max(zodKnownFieldDefaultValidation.stringMax)),
    orgStatus: z.record(z.string().uuid(), z.string().min(1).max(zodKnownFieldDefaultValidation.stringMax)),
    contactEmails: ContactEmailSchema.array().max(zodKnownFieldDefaultValidation.arrayMax).optional(),
    contactPhoneNumbers: ContactPhoneNumberSchema.array().max(zodKnownFieldDefaultValidation.arrayMax).optional(),
    emergencyContacts: EmergencyContactContainerSchema.array().max(zodKnownFieldDefaultValidation.arrayMax).optional(),
    pagerNumbers: PagerNumberSchema.array().max(zodKnownFieldDefaultValidation.arrayMax).optional(),
    consent: z.boolean(),
    captcha: z.string().optional(),
    bouncedEmails: z
        .string()
        .max(zodKnownFieldDefaultValidation.stringMax)
        .array()
        .max(zodKnownFieldDefaultValidation.arrayMax)
        .optional(),
    address: AddressSchema.optional(),
    lastLogin: z.number().optional(),
    deviceTokens: z
        .string()
        .max(zodKnownFieldDefaultValidation.stringMax)
        .array()
        .max(zodKnownFieldDefaultValidation.arrayMax)
        .optional(),
    registrationDate: z.number().optional(),
}));
export const accountDefaultColumns = defineColumns({
    email: {
        type: 'string-required',
        index: 'unique',
    },
    displayName: {
        type: 'string-required',
        index: true,
    },
    firstName: {
        type: 'string-required',
        index: 'partial',
    },
    lastName: {
        type: 'string-required',
        index: 'partial',
    },
    systemRole: {
        type: 'string-required',
        index: true,
    },
    systemStatus: {
        type: 'string-required',
        index: true,
    },
    // user is a member if has a role in organization
    orgMembership: {
        type: 'id-array-required', // orgId
        index: true,
    },
    // user has any status in organisation
    orgAssociation: {
        type: 'id-array-required', // orgId
        index: true,
    },
    orgRole: {
        type: 'string-array-required', // orgId_orgRole
        index: true,
    },
    orgRoleSet: {
        type: 'string-array-required', // orgRole
        index: true,
    },
    // used to validate maximum organizations per account, includes organization statuses with { appliesToMaxOrganizationsPerAccount: true }
    assignedOrgCount: {
        type: 'number',
        index: true,
    },
    hasOrgMembership: {
        type: 'boolean',
        index: true,
    },
    orgStatus: {
        type: 'string-array-required', // orgId_orgStatus
        index: true,
    },
    orgStatusSet: {
        type: 'string-array-required', // orgStatus
        index: true,
    },
    contactEmails: {
        type: 'string-array-required', // email
        index: true,
    },
    bouncedEmails: {
        type: 'string-array',
        index: true,
    },
    hasBouncedEmails: {
        type: 'boolean-required',
        index: true,
    },
    contactPhoneNumbers: {
        type: 'string-array-required', // phoneNumber
        index: true,
    },
    contactPhoneNumbersWithType: {
        type: 'string-array-required', // phoneNumber_phoneNumberType
        index: true,
    },
    contactPhoneNumbersWithTypeEncrypted: {
        type: 'string-array-required', // encrypted phoneNumber with type
        index: true,
    },
    contactPhoneNumberTypeSet: {
        type: 'string-array-required', // phoneNumberType
        index: true,
    },
    pagerNumbers: {
        type: 'string-array',
        index: true,
    },
    pagerNumbersEncrypted: {
        type: 'string-array',
        index: true,
    },
    pagerEmails: {
        type: 'string-array',
        index: true,
    },
    pagerEmailsEncrypted: {
        type: 'string-array',
        index: true,
    },
    contactMethodSet: {
        type: 'string-array-required',
        index: true,
    },
    hasUnconfirmedPhoneNumbers: {
        type: 'boolean-required',
        index: true,
    },
    hasCanceledPhoneNumbers: {
        type: 'boolean-required',
        index: true,
    },
    address: {
        type: 'string',
    },
    city: {
        type: 'string',
        index: 'partial',
    },
    postalCode: {
        type: 'string',
        index: true,
    },
    county: {
        type: 'string',
        index: true,
    },
    state: {
        type: 'string',
        index: true,
    },
    lastLogin: {
        type: 'number',
        index: true,
    },
    encryptionKeyHash: {
        type: 'string',
        index: true,
    },
    deviceTokens: {
        type: 'string-array-required',
        index: false,
    },
    search: {
        type: 'full-text-search-required',
        language: 'english',
    },
    partialSearch: {
        type: 'string-required',
        index: 'partial',
    },
    registrationDate: {
        type: 'number-required',
        index: true,
    },
});
